import { memo, useState } from 'react';
import { useLocation } from 'wouter';
import { useSelection } from '../lib/selection-context';
import { formatBytes } from '../lib/format-bytes';
import { CardActions } from './CardActions';
import { FavoriteStarButton } from './FavoriteStarButton';
import { StatusBadge } from './StatusBadge';
import { HighlightedText } from './HighlightedText';
import { AiGeneratedPill } from './AiGeneratedPill';
import { I } from '../lib/icons';
import type { AssetListItem } from '../lib/asset-types';

export type { AssetListItem };

function extBadge(filename: string, mime: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0) {
    const ext = filename.slice(dot + 1).toUpperCase();
    if (ext.length <= 4) return ext;
  }
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/webp': 'WEBP',
    'image/gif': 'GIF',
    'image/avif': 'AVIF',
    'image/svg+xml': 'SVG',
    'image/tiff': 'TIFF',
    'image/vnd.adobe.photoshop': 'PSD',
    'application/x-photoshop': 'PSD',
    'image/x-photoshop': 'PSD',
    'video/mp4': 'MP4',
    'video/quicktime': 'MOV',
    'application/pdf': 'PDF',
  };
  return mimeMap[mime.toLowerCase()] ?? 'FILE';
}

// Fallback-Farben wenn thumbUrl NULL (Sharp + S3-Upload beide fehlgeschlagen).
// video.bg (sehr dunkles Forest-Green) + pdf.bg (warmes Cream-Beige) haben
// keinen Brand-Token-Match (brand-green-900 / brand-sand sind nah aber
// sichtbarer Drift) → eigene @theme-Tokens --color-asset-video-bg/-pdf-bg.
const FALLBACK_BG: Record<AssetListItem['assetType'], string> = {
  img: 'bg-brand-green-200 text-brand-green-800',
  video: 'bg-asset-video-bg text-brand-yellow',
  pdf: 'bg-asset-pdf-bg text-brand-green-700',
  logo: 'bg-brand-green-700 text-brand-yellow',
};

type Props = {
  asset: AssetListItem;
  /**
   * Liefert die geladenen Asset-IDs in Grid-Reihenfolge — NUR zur Klick-Zeit
   * (Shift-Range) aufgerufen, nicht zum Rendern. Bewusst ein stabiler Callback
   * statt eines `allIds`-Arrays: ein Array-Prop wechselt bei jedem Accumulator-
   * Append die Identität und würde `memo(AssetCard)` für ALLE Cards brechen
   * (Re-Render-Storm pro nachgeladener Seite, Session 23). Fehlt der Callback,
   * fällt Range-Select sauber auf plain toggle zurück.
   */
  getAllIds?: () => readonly string[];
  /**
   * Rendering-Modus. `'library'` (default): Click navigiert zu `/assets/:id`,
   * CardActions zeigt Download/Open. `'trash'` (Sprint Papierkorb): Click =
   * direkt selection-toggle (kein navigate, weil GET /:id 404 für deleted
   * assets liefert). CardActions wird weggelassen weil Download/Open
   * ebenfalls 404 returnen. Restore + Permanent passieren via TrashActionBar
   * nach Multi-Select.
   */
  mode?: 'library' | 'trash';
  /**
   * Stapel-Modus: Klick auf ein Stapel-Tile (`asset.stackKey` + memberCount>1)
   * öffnet den Drill-in statt zum Asset zu navigieren. Bulk-Select selektiert
   * alle `asset.memberIds`. 1-Mitglied-„Stapel" verhält sich wie ein normales Tile.
   */
  onStackOpen?: (stackKey: string) => void;
  /**
   * Roher `?q=`-Suchstring fürs Treffer-Highlighting im Dateinamen. Bewusst ein
   * String-Primitive (NICHT ein abgeleitetes Token-Array): geht so durch
   * `memo(AssetCard)` ohne Identitäts-Wechsel → kein Re-Render-Storm
   * (Session 23, `patterns/react.md`). Leer/undefined → kein Highlight.
   */
  highlightQuery?: string;
  /**
   * Quick-Look (Session 30): `onMouseEnter` meldet die Asset-ID an einen
   * grid-level Ref (KEIN State → kein Re-Render). MUSS ein stabiler Callback
   * sein (`useCallback([])`), sonst bricht `memo(AssetCard)`.
   */
  onHover?: (id: string) => void;
  /**
   * Quick-Look öffnen (Auge-Button in CardActions). MUSS stabil sein (s. onHover).
   */
  onQuickLook?: (id: string) => void;
  /**
   * ⭐-Favoriten-Affordanz einblenden (Roadmap 3a Phase 1). Stabiles Boolean →
   * kein memo(AssetCard)-Break. Nur die Library setzt es; andere Surfaces
   * (Recipe/Collection/Dashboard/Add-Modals/Trash) lassen es weg (default off).
   * Greift nur für Einzel-Karten (nicht Stapel, nicht Trash).
   */
  enableFavorite?: boolean;
};

// Card-Wrapper-Restructure (Codex K1, kritisch):
// Buttons innerhalb eines `<a>` sind invalides HTML (nested-interactive).
// Hover-fähiges Gerät? (einmal beim Modul-Load, nicht pro Card). Desktop+Maus →
// CardActions lazy bei Hover/Focus; Touch (kein Hover) → sofort gemountet.
const HOVERABLE =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Card-Root ist `<div className="group relative">`, mit `<a>` für Card-
// Click + `<CardActions>` + Selection-Checkbox als siblings. Alle 3 sind
// absolute-positioned (CardActions + Checkbox) bzw. block (`<a>`). Click
// auf `<a>` triggert navigate / select wie bisher; CardActions hat eigene
// stopPropagation damit Quick-Action-Click nicht parallel die Card-Click-
// Logic feuert.
function AssetCardComponent({ asset, getAllIds, mode = 'library', onStackOpen, highlightQuery, onHover, onQuickLook, enableFavorite = false }: Props) {
  const [, navigate] = useLocation();
  const selection = useSelection();
  const badge = extBadge(asset.displayFilename, asset.mimeType);
  const selectionActive = selection.count > 0;
  const isTrash = mode === 'trash';

  // Perf: CardActions (Download/Open-Buttons) ist der teuerste Card-Teil
  // (eigener useLocation + Effekte + SVGs) und auf Desktop NUR bei Hover
  // sichtbar. Im virtualisierten Grid mountet jede Card beim Scrollen — daher
  // CardActions auf Hover-Geräten erst bei Hover/Focus mounten (~38 % weniger
  // Scroll-Scripting). Touch-Geräte (kein Hover) brauchen den „…"-Button immer →
  // dort sofort gemountet. Einmal gemountet bleibt es (kein Unmount-Churn).
  const [showActions, setShowActions] = useState(!HOVERABLE);

  // Stapel-Tile: mehr als 1 Mitglied → Drill-in statt Asset-Navigation, Bulk-
  // Select über alle memberIds. 1-Mitglied-„Stapel" = normales Tile.
  const isStack = asset.stackKey != null && (asset.memberCount ?? 1) > 1;
  const memberIds = asset.memberIds;
  const canBulkSelect = isStack && !asset.bulkSelectCapped && (memberIds?.length ?? 0) > 0;
  const isSelected =
    isStack ? canBulkSelect && memberIds!.every((id) => selection.has(id)) : selection.has(asset.id);

  // Selektion: Stapel → alle (filtered) memberIds via setMany; Einzel → toggle/range.
  function applySelection(range: boolean): void {
    if (isStack) {
      if (canBulkSelect && memberIds) selection.setMany(memberIds, !isSelected);
      return; // bulkSelectCapped → no-op (Tile zu groß für Sammelauswahl)
    }
    if (range && getAllIds) selection.selectRange(asset.id, getAllIds());
    else selection.toggle(asset.id);
  }

  // Click-Pattern (iOS-Photos):
  //   - Cmd/Ctrl-Click → always toggle (independent of mode)
  //   - Shift-Click → range select (always)
  //   - Plain click in selection-mode (count>=1) → toggle
  //   - Plain click out-of-mode → navigate (let <a> default through)
  //
  // Trash-Variante: plain click = always toggle (kein navigate, weil
  // GET /api/assets/:id 404 returns für deleted assets — siehe Spec
  // Sprint Papierkorb).
  function handleClick(
    e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ): void {
    if (e.shiftKey) {
      e.preventDefault();
      applySelection(true);
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      applySelection(false);
      return;
    }
    if (selectionActive || isTrash) {
      e.preventDefault();
      applySelection(false);
      return;
    }
    e.preventDefault();
    // Stapel-Tile → Drill-in; sonst Asset-Detail.
    if (isStack && asset.stackKey && onStackOpen) {
      onStackOpen(asset.stackKey);
      return;
    }
    navigate(`/assets/${asset.id}`);
  }

  // Trash-Variante rendert <div role="button"> statt <a href>, weil ein
  // Asset im Papierkorb keine valide /assets/:id-URL hat (returnt 404).
  // Middle-click / new-tab gibt's hier folglich nicht — by design.
  // Stapel-Tile + Trash rendern <div role=button/option> (kein <a href>): ein
  // Stapel hat keine valide /assets/:id-URL (Klick = Drill-in), ein deleted Asset
  // returnt 404. Nur Einzel-Asset (library) bleibt navigierbarer <a>.
  const useDiv = isTrash || isStack;
  const RootTag = useDiv ? 'div' : 'a';
  const rootProps = useDiv
    ? {
        role: (selectionActive || isTrash ? 'option' : 'button') as 'option' | 'button',
        tabIndex: 0,
        'aria-selected': selectionActive || isTrash ? isSelected : undefined,
        onClick: handleClick,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (selectionActive || isTrash) applySelection(false);
            else if (isStack && asset.stackKey && onStackOpen) onStackOpen(asset.stackKey);
          }
        },
      }
    : {
        href: `/assets/${asset.id}`,
        onClick: handleClick,
        role: selectionActive ? ('option' as const) : ('link' as const),
        'aria-selected': selectionActive ? isSelected : undefined,
      };

  return (
    <div
      className="group relative"
      data-asset-card
      // Lazy-Mount-Trigger für CardActions (nur auf Hover-Geräten relevant) +
      // Quick-Look-Hover-Meldung (Session 30): schreibt die ID in einen grid-
      // level Ref via stabilen `onHover` — KEIN State hier, kein Re-Render.
      onMouseEnter={() => {
        onHover?.(asset.id);
        if (HOVERABLE) setShowActions(true);
      }}
      onFocus={HOVERABLE ? () => setShowActions(true) : undefined}
    >
      <RootTag
        {...(rootProps as React.HTMLAttributes<HTMLElement> & { href?: string })}
        className="block text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2 rounded-lg cursor-pointer transition-transform duration-200 ease-out group-hover:-translate-y-1"
      >
        {/* Redesign S51: Hover-Lift (RootTag) + Elevation auf dem Thumb-Slot. */}
        <div
          // S2: ausgewählt = 3px-Ring brand-green-700 + 12 %-Tint (Overlay unten).
          className={`aspect-[4/3] rounded-lg overflow-hidden relative bg-ink-100 mb-2 transition group-hover:shadow-elevated ${
            isSelected ? 'ring-[3px] ring-brand-green-700' : ''
          }`}
          // Stapel-Optik: zwei versetzte „Karten dahinter" via box-shadow
          // (overflow-hidden clippt nur das Bild, nicht den Schatten).
          style={
            isStack
              ? {
                  boxShadow:
                    '3px 3px 0 0 var(--color-brand-green-200), 6px 6px 0 0 var(--color-brand-green-100)',
                }
              : undefined
          }
        >
          {asset.thumbUrl ? (
            <img
              src={asset.thumbUrl}
              alt=""
              loading="lazy"
              // decoding=async: Bild-Decode off-main-thread → kein Scroll-Jank
              // beim Mounten neuer Cards im virtualisierten Grid. Der frühere
              // React-State-Fade (imgLoaded) ist raus: er erzwang pro Bild-Load
              // einen Re-Render (×N sichtbare Cards über die Ladezeit → genau das
              // Scroll-Ruckeln). Der bg-ink-100-Slot dient als Platzhalter bis
              // das Bild da ist.
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className={`w-full h-full flex items-center justify-center font-mono font-bold text-2xl ${FALLBACK_BG[asset.assetType]}`}
            >
              {badge}
            </div>
          )}
          {/* Play-Overlay: IMMER bei assetType video (auch auf Platzhalter) —
              seit echte Poster-Frames existieren, wären Videos sonst nicht
              mehr von Fotos unterscheidbar (Spec Video-Thumbs MH 6).
              pointer-events-none → Card-Klick/Selection bleiben durchlässig. */}
          {asset.assetType === 'video' && (
            <div
              data-play-overlay
              className="pointer-events-none absolute inset-0 grid place-items-center"
            >
              <span className="grid place-items-center rounded-full bg-brand-green-900/55 p-2 text-white shadow-sm">
                <I.play size={20} aria-hidden />
              </span>
            </div>
          )}
          {/* KI-Kennzeichnung (Transparenzpflicht). Unten LINKS — oben rechts
              sitzt die Checkbox, unten rechts das Stapel-Badge. */}
          {asset.aiGenerated && <AiGeneratedPill variant="thumbnail" />}
          {isSelected && (
            <div
              data-selected-tint
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-brand-green-700/12"
            />
          )}
          {/* Stapel-Count-Badge (nur memberCount>1) — Anzahl im Filterkontext. */}
          {isStack && (
            <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-brand-green-900/80 px-1.5 py-0.5 text-2xs font-semibold text-brand-cream shadow-sm">
              <I.stack size={11} />
              {asset.memberCount}
            </span>
          )}
        </div>
        <div
          className="text-xs font-semibold text-brand-green-700 truncate leading-tight"
          // title bleibt Plain-Text (kein <mark>) — Tooltip + a11y unverändert.
          title={isStack ? (asset.stackLabel ?? asset.displayFilename) : asset.displayFilename}
        >
          <HighlightedText
            text={isStack ? (asset.stackLabel ?? asset.displayFilename) : asset.displayFilename}
            query={highlightQuery}
          />
        </div>
        {/* Meta unter dem Dateinamen (Session 10): nur noch Typ · Dateigröße,
            kleiner als der Dateiname (text-2xs=12px vs filename text-xs=13px).
            Uploader/Zeit + Tag-Pills bewusst entfernt — Card entschlackt. */}
        <div className="text-2xs text-brand-green-600 mt-0.5 font-mono truncate">
          {badge} · {formatBytes(asset.fileSize)}
        </div>
      </RootTag>

      {/* Status-Badge sibling (Sprint Quick-Wins Item 2). Top-left neben
          dem extension-badge. Approved → null (kein Badge). */}
      <StatusBadge status={asset.status} />

      {/* Quick-Actions sibling (NICHT nested im <a>, Codex K1 invalid HTML).
          CardActions rendert NICHTS wenn selectionActive — Selection-Checkbox
          bleibt einziger Action-Slot.
          Trash-Mode: CardActions weggelassen — Download/Open würden 404 für
          deleted assets liefern. Restore + Permanent passieren via
          TrashActionBar nach Multi-Select. */}
      {showActions && !isTrash && !isStack && (
        // Positions-Ebene exakt über dem Thumbnail (inset-x-0 top-0 aspect-[4/3]),
        // damit CardActions' Download-Button unten rechts am BILD ankert (nicht am
        // unteren Karten-Rand unter den Textzeilen). pointer-events-none → Card-Klick
        // bleibt durchlässig; die interaktiven Buttons re-aktivieren pointer-events.
        <div className="pointer-events-none absolute inset-x-0 top-0 aspect-[4/3]">
          <CardActions
            assetId={asset.id}
            actionsActive={!selectionActive}
            assetType={asset.assetType}
            onQuickLook={onQuickLook}
          />
        </div>
      )}

      {/* ⭐-Favoriten-Toggle (Roadmap 3a Phase 1) — in einer Thumbnail-Positions-Ebene
          (inset-x-0 top-0 aspect-[4/3], wie CardActions), damit der Stern unten links
          AM BILD ankert (nicht am Karten-Boden unter dem Dateinamen). top-left =
          Quick-Look-Auge, top-right = Checkbox, bottom-right = Download → unten links
          ist frei (kein Hover-Overlap mehr). pointer-events-none → Card-Klick
          durchlässig; der Button re-aktiviert sie. Eigener Component → useToast/Hook
          läuft nur hier (Library). Stapel/Trash → kein Stern. */}
      {enableFavorite && !isStack && !isTrash && (
        <div className="pointer-events-none absolute inset-x-0 top-0 aspect-[4/3]">
          <FavoriteStarButton assetId={asset.id} favorited={asset.favorited} variant="card" />
        </div>
      )}

      {/* Selection-Checkbox — KLICKBARER sibling zu <a> + CardActions. Ein Klick
          selektiert das Asset OHNE ⌘ (Discoverability — das versteckte ⌘-Klick-
          Muster war nicht auffindbar). Shift-Klick = Range. stopPropagation +
          preventDefault verhindern, dass der Card-<a> parallel navigiert.
          Sichtbarkeit: immer wenn selectionActive (count>=1) / Trash / selektiert /
          fokussiert, sonst on-hover — aber nur auf Geräten mit echtem Hover
          (`md:hoverable:`); Touch-Tablets/Mobile zeigen sie dauerhaft.
          Selected: dark-green filled mit white check. Unselected: white border.
          S2 (2026-10-10): oben LINKS (Favorit/Aktionen oben rechts), 44px-Hit-
          Area mit 24px-Box (Touch-Ziel ≥ 44px). */}
      <button
        type="button"
        aria-label={isSelected ? `„${asset.displayFilename}" abwählen` : `„${asset.displayFilename}" auswählen`}
        aria-pressed={isSelected}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          applySelection(e.shiftKey);
        }}
        className={`group/check absolute top-0 left-0 w-11 h-11 flex items-center justify-center transition-opacity cursor-pointer focus:outline-none ${
          selectionActive || isSelected || isTrash
            ? 'opacity-100'
            : 'opacity-100 md:hoverable:opacity-0 md:hoverable:group-hover:opacity-100 focus-visible:opacity-100'
        }`}
      >
        <span
          className={`w-6 h-6 rounded-md flex items-center justify-center shadow-sm group-focus-visible/check:ring-2 group-focus-visible/check:ring-brand-green-700 group-focus-visible/check:ring-offset-1 ${
            isSelected
              ? 'bg-brand-green-700 text-white'
              : 'bg-white/85 text-transparent border-2 border-white hover:border-brand-green-700'
          }`}
        >
          <I.check size={14} />
        </span>
      </button>
    </div>
  );
}

// Memoized: beim Infinite-Scroll-Append wächst der Accumulator → AssetGrid
// re-rendert mit der vollen Liste. Ohne wirksame memo würde JEDE bereits
// geladene Card pro Append neu rendern (O(N²) Reconciliation → der gemessene
// Re-Render-Storm, Session 23). Props sind über Appends stabil: `asset` (gleiche
// Ref aus dem Accumulator), `getAllIds` (ref-backed Callback, konstante
// Identität), `mode` (Literal), `onStackOpen` (useCallback), `enableFavorite`
// (stabiles Boolean) → nur die NEU angehängten Cards mounten. Selektion läuft über Context (umgeht memo →
// Selected-Ring aktualisiert weiterhin korrekt).
export const AssetCard = memo(AssetCardComponent);
